/**
 * useWarehouses — F1 (plan „shared frontend foundations for the product-picker
 * modal"). Fetches the warehouses list for the future warehouse multi-select
 * (`WarehouseMultiSelect`), mirroring the graceful module-gating degradation
 * pattern used by `ProductSearchPicker`'s products query: the warehouses
 * endpoint can be gated behind a module (402) or a missing permission (403)
 * for tenants without it — that resolves to an empty list rather than
 * surfacing `isError`. Any OTHER error (network, 500, etc.) is rethrown.
 *
 * Endpoint: GET /warehouses?companyId=<companyId> (Model B company-scoped
 * listing — `companyId` omitted entirely when unscoped, not sent as empty).
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { isModuleGatingError, MODULE_GATED_LIST_STALE_TIME_MS } from '@/lib/module-gating';

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface WarehousesListResponse {
  warehouses: Warehouse[];
  limit: number;
  offset: number;
  total: number;
}

export interface UseWarehousesParams {
  companyId?: string | null;
  /** Default true — pass false to gate the fetch to when a modal is open. */
  enabled?: boolean;
}

export function useWarehouses({ companyId, enabled = true }: UseWarehousesParams) {
  const query = useQuery<WarehousesListResponse>({
    queryKey: ['warehouses', companyId ?? null],
    queryFn: async () => {
      try {
        const url = companyId
          ? `/warehouses?${new URLSearchParams({ companyId }).toString()}`
          : '/warehouses';
        const res = await api.get<WarehousesListResponse>(url);
        return res.data;
      } catch (err) {
        // Moduł magazynowy może być nieaktywny (402) albo brak uprawnienia (403) —
        // degradujemy do pustej listy zamiast wywalać multi-select. INNE błędy
        // (sieć, 500) rzucamy dalej — mają wpaść w isError.
        if (isModuleGatingError(err)) {
          return { warehouses: [], limit: 0, offset: 0, total: 0 };
        }
        throw err;
      }
    },
    enabled,
    staleTime: MODULE_GATED_LIST_STALE_TIME_MS,
  });

  return {
    warehouses: query.data?.warehouses ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
